import { useState, useEffect } from "react";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { 
  Code2, 
  Plus, 
  Copy, 
  Trash2, 
  ExternalLink,
  Loader2,
  Users,
  Calendar,
  BarChart3,
  Link2
} from "lucide-react";

export default function Embeds() {
  const { user } = useAuth();
  const [embeds, setEmbeds] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  
  // Create form state
  const [selectedScenario, setSelectedScenario] = useState("");
  const [embedName, setEmbedName] = useState("");

  const fetchData = async () => {
    try {
      const [embedsRes, scenariosRes, templatesRes] = await Promise.all([
        fetch(`${API}/embeds`, { credentials: "include" }),
        fetch(`${API}/scenarios`, { credentials: "include" }),
        fetch(`${API}/templates`, { credentials: "include" })
      ]);

      if (embedsRes.ok) {
        const data = await embedsRes.json();
        setEmbeds(data);
      }
      
      if (scenariosRes.ok) {
        const data = await scenariosRes.json();
        setScenarios(data);
      }
      
      if (templatesRes.ok) {
        const data = await templatesRes.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load embeds");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateEmbed = async () => {
    if (!selectedScenario) {
      toast.error("Please select a scenario");
      return;
    }

    setCreating(true);
    try {
      const res = await fetch(`${API}/embeds`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          scenario_id: selectedScenario,
          name: embedName || undefined
        })
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed to create embed");
      }

      const newEmbed = await res.json();
      setEmbeds(prev => [newEmbed, ...prev]);
      setShowCreateDialog(false);
      setSelectedScenario("");
      setEmbedName("");
      toast.success("Embed created successfully!");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteEmbed = async (embedId) => {
    if (!confirm("Are you sure you want to delete this embed?")) return;

    try {
      const res = await fetch(`${API}/embeds/${embedId}`, {
        method: "DELETE",
        credentials: "include"
      });

      if (!res.ok) throw new Error("Failed to delete embed");

      setEmbeds(prev => prev.filter(e => e.embed_id !== embedId));
      toast.success("Embed deleted");
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleToggleActive = async (embedId, currentActive) => {
    try {
      const res = await fetch(`${API}/embeds/${embedId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ active: !currentActive })
      });

      if (!res.ok) throw new Error("Failed to update embed");

      const updated = await res.json();
      setEmbeds(prev => prev.map(e => e.embed_id === embedId ? updated : e));
      toast.success(updated.active ? "Embed activated" : "Embed deactivated");
    } catch (error) {
      toast.error(error.message);
    }
  };

  const getEmbedUrl = (embedId) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/embed/${embedId}`;
  };

  const getIframeCode = (embedId) => {
    const url = getEmbedUrl(embedId);
    return `<iframe src="${url}" width="100%" height="700" frameborder="0" allow="microphone"></iframe>`;
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  // Combine scenarios and templates for selection
  const allScenarios = [
    ...scenarios.map(s => ({ ...s, id: s.scenario_id, type: "scenario" })),
    ...templates.map(t => ({ ...t, id: t.template_id, type: "template" }))
  ];

  if (!user?.is_manager && !user?.is_admin) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Manager Access Required</h2>
            <p className="text-muted-foreground">
              Only managers and admins can create and manage embeds.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="embeds-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">Scenario Embeds</h1>
          <p className="text-muted-foreground mt-1">
            Create embeddable scenarios for external websites
          </p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button data-testid="create-embed-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Embed
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Embed</DialogTitle>
              <DialogDescription>
                Select a scenario to create an embeddable widget for external websites.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Scenario</Label>
                <Select value={selectedScenario} onValueChange={setSelectedScenario}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a scenario" />
                  </SelectTrigger>
                  <SelectContent>
                    {allScenarios.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name} {s.type === "template" && "(Template)"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Embed Name (optional)</Label>
                <Input
                  placeholder="e.g., Sales Training Course"
                  value={embedName}
                  onChange={(e) => setEmbedName(e.target.value)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateEmbed} disabled={creating || !selectedScenario}>
                {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Create Embed
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Embeds List */}
      {embeds.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Code2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Embeds Yet</h3>
            <p className="text-muted-foreground mb-4">
              Create your first embed to share scenarios on external websites.
            </p>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Embed
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {embeds.map((embed) => (
            <Card key={embed.embed_id} className={!embed.active ? "opacity-60" : ""}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      {embed.name}
                      {!embed.active && (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </CardTitle>
                    <CardDescription className="mt-1">
                      Scenario: {embed.scenario_name}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={embed.active}
                      onCheckedChange={() => handleToggleActive(embed.embed_id, embed.active)}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive"
                      onClick={() => handleDeleteEmbed(embed.embed_id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Stats */}
                <div className="flex items-center gap-6 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Calendar className="w-4 h-4" />
                    Created {formatDate(embed.created_at)}
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <BarChart3 className="w-4 h-4" />
                    {embed.usage_count || 0} uses
                  </div>
                </div>

                {/* URL */}
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Direct Link</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      readOnly
                      value={getEmbedUrl(embed.embed_id)}
                      className="font-mono text-xs"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => copyToClipboard(getEmbedUrl(embed.embed_id), "URL")}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => window.open(getEmbedUrl(embed.embed_id), "_blank")}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Iframe Code */}
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Embed Code</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      readOnly
                      value={getIframeCode(embed.embed_id)}
                      className="font-mono text-xs"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => copyToClipboard(getIframeCode(embed.embed_id), "Embed code")}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
